import type { ReactElement } from 'react';
import './ui.css';

/**
 * Inline stroke icons. Drawn on a 24×24 grid with `currentColor` so a single
 * definition works on tinted chips, dark KPI tiles and plain text alike, and
 * the panel gains no icon-font or SVG-sprite dependency.
 */
export type IconName =
  // Modules / navigation
  | 'dashboard'
  | 'chart'
  | 'users'
  | 'robot'
  | 'post'
  | 'group'
  | 'star'
  | 'shield'
  | 'alert'
  | 'shieldUser'
  | 'megaphone'
  | 'settings'
  | 'flag'
  // People
  | 'userPlus'
  | 'userCheck'
  | 'userX'
  | 'userCog'
  // Content / metrics
  | 'activity'
  | 'image'
  | 'heart'
  | 'comment'
  | 'share'
  | 'clock'
  | 'calendar'
  | 'monitor'
  | 'smartphone'
  | 'tablet'
  | 'trendUp'
  | 'list'
  | 'history'
  // Row + toolbar actions
  | 'view'
  | 'eyeOff'
  | 'edit'
  | 'trash'
  | 'key'
  | 'ban'
  | 'power'
  | 'check'
  | 'close'
  | 'refresh'
  | 'search'
  | 'filter'
  | 'plus'
  | 'download'
  | 'mail'
  | 'wallet'
  | 'link'
  | 'externalLink'
  | 'copy'
  // Chrome
  | 'menu'
  | 'sidebar'
  | 'sun'
  | 'moon'
  | 'logOut'
  | 'arrowLeft'
  | 'arrowRight'
  | 'chevronLeft'
  | 'chevronRight'
  | 'chevronDown';

/**
 * Hue families. A module or action keeps the same tone everywhere it appears —
 * the rail, the breadcrumb chip and the row action all read as one thing — and
 * the semantic aliases exist so a destructive action never has to hardcode
 * `rose`.
 */
export type IconTone =
  | 'default'
  | 'blue'
  | 'indigo'
  | 'violet'
  | 'teal'
  | 'emerald'
  | 'amber'
  | 'rose'
  | 'cyan'
  | 'slate'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info';

export type IconSize = 'sm' | 'md' | 'lg';

const PATHS: Record<IconName, ReactElement> = {
  dashboard: (
    <>
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </>
  ),
  chart: (
    <>
      <path d="M3 3v18h18" />
      <rect x="7" y="12" width="3" height="6" rx="1" />
      <rect x="12" y="8" width="3" height="10" rx="1" />
      <rect x="17" y="5" width="3" height="13" rx="1" />
    </>
  ),
  users: (
    <>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  robot: (
    <>
      <rect x="4" y="8" width="16" height="12" rx="3" />
      <path d="M12 4v4M9 2h6" />
      <circle cx="9" cy="13" r="1" />
      <circle cx="15" cy="13" r="1" />
      <path d="M9.5 17h5M2 12v4M22 12v4" />
    </>
  ),
  post: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <path d="M8 13h8M8 17h5" />
    </>
  ),
  group: (
    <>
      <circle cx="9" cy="8" r="3" />
      <circle cx="17" cy="10" r="2.5" />
      <path d="M3 20v-1.5A4.5 4.5 0 0 1 7.5 14h3A4.5 4.5 0 0 1 15 18.5V20" />
      <path d="M17 15h.5A3.5 3.5 0 0 1 21 18.5V20" />
    </>
  ),
  star: (
    <polygon points="12 2.5 15 9 22 9.8 17 14.4 18.3 21.2 12 17.9 5.7 21.2 7 14.4 2 9.8 9 9" />
  ),
  shield: (
    <>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11.5 14.5 16 10" />
    </>
  ),
  alert: (
    <>
      <path d="M10.3 3.9 1.9 18a2 2 0 0 0 1.7 3h16.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      <path d="M12 9v4M12 17h.01" />
    </>
  ),
  shieldUser: (
    <>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <circle cx="12" cy="10" r="2.5" />
      <path d="M7.8 17a4.5 4.5 0 0 1 8.4 0" />
    </>
  ),
  megaphone: (
    <>
      <path d="M3 11v2a1 1 0 0 0 1 1h2l9 5V5L6 10H4a1 1 0 0 0-1 1z" />
      <path d="M18.5 8.5a5 5 0 0 1 0 7" />
      <path d="M7 14v5" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 7 19.4l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 3 13.7H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 7l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 10 3V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 2.9 1.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0 1.2 2.9H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </>
  ),
  flag: (
    <>
      <path d="M4 21V4M4 4h11l-1.5 4L15 12H4" />
    </>
  ),
  userPlus: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <path d="M20 8v6M23 11h-6" />
    </>
  ),
  userCheck: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <polyline points="17 11 19 13 23 9" />
    </>
  ),
  userX: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <path d="M18 9l4 4M22 9l-4 4" />
    </>
  ),
  userCog: (
    <>
      <path d="M12 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="6.5" cy="7" r="4" />
      <circle cx="18" cy="15" r="3" />
      <path d="M18 10v2M18 18v2M13.7 12.5l1.7 1M20.6 16.5l1.7 1M13.7 17.5l1.7-1M20.6 13.5l1.7-1" />
    </>
  ),
  activity: <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />,
  image: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </>
  ),
  heart: (
    <path d="M12 20.3l-7.1-7A4.4 4.4 0 0 1 12 7.6a4.4 4.4 0 0 1 7.1 5.7z" />
  ),
  comment: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  share: (
    <>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </>
  ),
  monitor: (
    <>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </>
  ),
  smartphone: (
    <>
      <rect x="5" y="2" width="14" height="20" rx="2" />
      <path d="M12 18h.01" />
    </>
  ),
  tablet: (
    <>
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M12 18h.01" />
    </>
  ),
  trendUp: (
    <>
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </>
  ),
  list: (
    <>
      <path d="M8 6h13M8 12h13M8 18h13" />
      <path d="M3 6h.01M3 12h.01M3 18h.01" />
    </>
  ),
  history: (
    <>
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5" />
      <polyline points="12 8 12 12 15 14" />
    </>
  ),
  view: (
    <>
      <path d="M1.5 12S5 5.5 12 5.5 22.5 12 22.5 12 19 18.5 12 18.5 1.5 12 1.5 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  eyeOff: (
    <>
      <path d="M10.6 6.1A8.9 8.9 0 0 1 12 6c7 0 10.5 6 10.5 6a17.7 17.7 0 0 1-2.7 3.4M6.4 7.9A17.4 17.4 0 0 0 1.5 12s3.5 6 10.5 6a9.6 9.6 0 0 0 3.6-.7" />
      <path d="M10 10a2.8 2.8 0 0 0 4 4" />
      <path d="M3 3l18 18" />
    </>
  ),
  edit: (
    <>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
    </>
  ),
  trash: (
    <>
      <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
    </>
  ),
  key: (
    <>
      <circle cx="7.5" cy="15.5" r="4.5" />
      <path d="M10.8 12.2 21 2M16 3l3 3M13.5 5.5l3 3" />
    </>
  ),
  ban: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M5.6 5.6l12.8 12.8" />
    </>
  ),
  power: (
    <>
      <path d="M18.4 6.6a9 9 0 1 1-12.8 0" />
      <path d="M12 2v9" />
    </>
  ),
  check: <polyline points="4 12.5 9.5 18 20 6.5" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  refresh: (
    <>
      <path d="M21 12a9 9 0 1 1-2.6-6.4L21 8" />
      <polyline points="21 3 21 8 16 8" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M16.5 16.5 21 21" />
    </>
  ),
  filter: <polygon points="21 4 3 4 10 12.5 10 19 14 21 14 12.5" />,
  plus: <path d="M12 5v14M5 12h14" />,
  download: (
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <path d="M12 15V3" />
    </>
  ),
  mail: (
    <>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <polyline points="2.5 6 12 13 21.5 6" />
    </>
  ),
  wallet: (
    <>
      <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
      <path d="M2.5 10h19" />
      <path d="M16.5 14.5h2" />
    </>
  ),
  link: (
    <>
      <path d="M10 13a5 5 0 0 0 7.1 0l2.4-2.4a5 5 0 0 0-7.1-7.1L11 4.9" />
      <path d="M14 11a5 5 0 0 0-7.1 0L4.5 13.4a5 5 0 0 0 7.1 7.1L13 19.1" />
    </>
  ),
  externalLink: (
    <>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <path d="M10 14 21 3" />
    </>
  ),
  copy: (
    <>
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
    </>
  ),
  menu: <path d="M3 6h18M3 12h18M3 18h18" />,
  sidebar: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9.5 4v16" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4.5" />
      <path d="M12 1.5v2M12 20.5v2M3.5 12h-2M22.5 12h-2M5.6 5.6 4.2 4.2M19.8 19.8l-1.4-1.4M18.4 5.6l1.4-1.4M4.2 19.8l1.4-1.4" />
    </>
  ),
  moon: <path d="M21 13.2A9 9 0 1 1 10.8 3a7 7 0 0 0 10.2 10.2z" />,
  logOut: (
    <>
      <path d="M10 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
      <polyline points="16 16 21 12 16 8" />
      <path d="M21 12H9" />
    </>
  ),
  arrowLeft: (
    <>
      <path d="M19 12H5" />
      <polyline points="12 5 5 12 12 19" />
    </>
  ),
  arrowRight: (
    <>
      <path d="M5 12h14" />
      <polyline points="12 5 19 12 12 19" />
    </>
  ),
  chevronLeft: <polyline points="15 5 8 12 15 19" />,
  chevronRight: <polyline points="9 5 16 12 9 19" />,
  chevronDown: <polyline points="5 9 12 16 19 9" />,
};

interface IconProps {
  name: IconName;
  /** Hue family; `default` inherits the surrounding text colour. */
  tone?: IconTone;
  size?: IconSize;
  className?: string;
}

/**
 * Always decorative: every caller pairs the icon with a visible text label or
 * an accessible name on the control that owns it, so it is hidden from
 * assistive technology rather than given a duplicate name.
 */
export function Icon({ name, tone = 'default', size = 'md', className }: IconProps) {
  const classes = ['ui-icon', `ui-icon--${size}`];
  if (tone !== 'default') {
    classes.push(`ui-icon--${tone}`);
  }
  if (className) {
    classes.push(className);
  }

  return (
    <svg
      className={classes.join(' ')}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false">
      {PATHS[name]}
    </svg>
  );
}
